import React, { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Stack,
  Paper,
  CircularProgress,
  Chip,
  Grid,
  Divider,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import useResourceVoucher from '../../hooks/useResourceVoucher';
import { getStatusLabel } from '../../util/paymentUtils';
import BookingDetailsModal from '../Bookings/BookingDetailsModal/BookingDetailsModal';
import { Button } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useAuth } from '../../context/authContext';
import { toast } from 'react-toastify';
import LockIconWrapper from '../components/LockIconWrapper';
import VoucherEmailSection from './VoucherEmailSection';

function ResourceVoucherDetailsModal({ open, onClose, voucherId }) {
  const {
    getResourceVoucherById,
    getResourceVoucherDownloadURL,
    resendResourceVoucherEmail,
    updateResourceVoucherBuyerEmail,
  } = useResourceVoucher();
  const { checkRightAny, currentUser } = useAuth();
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    const fetchVoucher = async () => {
      if (!open || !voucherId) {
        setVoucher(null);
        return;
      }
      setLoading(true);
      try {
        const result = await getResourceVoucherById(voucherId);
        setVoucher(result);
      } catch (err) {
        console.error('Error fetching resource voucher:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchVoucher();
  }, [voucherId, open]);

  const getStatusColor = status => {
    switch (status) {
      case 'active':
        return 'success';
      case 'waitingForPayment':
        return 'warning';
      case 'used':
        return 'info';
      case 'locked':
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatUser = user => {
    if (!user) return '-';
    if (typeof user === 'string') return user;
    const { firstName, lastName, username } = user || {};
    if (firstName && lastName) return `${firstName} ${lastName}`;
    return username || '-';
  };

  const createdByDisplay = voucher?.meta?.createdByString || formatUser(voucher?.meta?.createdBy);
  const editedByDisplay = formatUser(voucher?.meta?.editedBy);

  const isCreator = voucher?.meta?.createdBy?._id === currentUser?.userId;
  const canDownload = checkRightAny('voucher:special:download') || isCreator;

  const handleDownloadPdf = async () => {
    if (!voucher || voucher.status === 'waitingForPayment') {
      toast.error('Gutschein muss erst bezahlt werden, bevor er heruntergeladen werden kann.');
      return;
    }
    const toastMessage = `Die Download-Anfrage für Gutschein ${voucher.voucherCode} wurde gespeichert und kann später in den offenen Anfragen bestätigt werden`;
    const displayInfo = {
      Typ: 'Produktgutschein PDF Download',
      Gutscheincode: voucher.voucherCode,
      Status: getStatusLabel(voucher.status),
    };
    const onStored = response => {
      if (response && response.preSignedURL) {
        window.open(response.preSignedURL, '_blank');
      }
      onClose();
    };
    setPdfLoading(true);
    try {
      const response = await getResourceVoucherDownloadURL(
        voucher._id,
        toastMessage,
        onStored,
        displayInfo
      );
      if (response && response.preSignedURL) {
        window.open(response.preSignedURL, '_blank');
      }
    } catch (err) {
      toast.error('Fehler beim Download des Gutscheins');
      console.error('Unexpected error during resource voucher download:', err);
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby='resource-voucher-details-modal'
        aria-describedby='resource-voucher-details-description'
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            overflow: 'auto',
            borderRadius: '10px',
          }}
        >
          {loading ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}
            >
              <CircularProgress />
            </Box>
          ) : !voucher ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}
            >
              <Typography variant='body2'>Kein Gutschein gefunden.</Typography>
            </Box>
          ) : (
            <>
              <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
                <Typography variant='h5'>Produktgutscheine: {voucher.voucherCode}</Typography>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Chip
                    label={getStatusLabel(voucher.status)}
                    color={getStatusColor(voucher.status)}
                    size='small'
                  />
                  <IconButton onClick={onClose}>
                    <CloseIcon />
                  </IconButton>
                </Stack>
              </Stack>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Stack spacing={1}>
                      {voucher.resourceVoucherProductId?.name && (
                        <Typography variant='body2'>
                          <strong>PG-Produkt:</strong> {voucher.resourceVoucherProductId.name}
                        </Typography>
                      )}
                      <Typography variant='body2'>
                        <strong>Gutscheincode:</strong> {voucher.voucherCode}
                      </Typography>
                      <Typography variant='body2'>
                        <strong>Status:</strong> {getStatusLabel(voucher.status)}
                      </Typography>
                      <Typography variant='body2'>
                        <strong>Ablaufdatum:</strong>{' '}
                        {voucher.expirationDate
                          ? dayjs(voucher.expirationDate).format('DD.MM.YYYY')
                          : '-'}
                      </Typography>
                      <VoucherEmailSection
                        email={voucher.buyerMail}
                        status={voucher.status}
                        onResend={() => resendResourceVoucherEmail(voucher._id)}
                        onEdit={email => updateResourceVoucherBuyerEmail(voucher._id, email)}
                        onEmailUpdated={email =>
                          setVoucher(prev => ({ ...prev, buyerMail: email }))
                        }
                      />
                      {voucher.buyerPostcode && (
                        <Typography variant='body2'>
                          <strong>Käufer PLZ:</strong> {voucher.buyerPostcode}
                        </Typography>
                      )}
                      {voucher.creationComment && (
                        <Typography variant='body2'>
                          <strong>Erstellungskommentar:</strong> {voucher.creationComment}
                        </Typography>
                      )}
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Stack spacing={1}>
                      <Typography variant='body2'>
                        <strong>Dauer:</strong>{' '}
                        {`${Math.round((voucher.durationMS || 0) / 60000)} Min`}
                      </Typography>
                      {'personCount' in voucher && (
                        <Typography variant='body2'>
                          <strong>Personen:</strong> {voucher.personCount}
                        </Typography>
                      )}
                      {voucher.relatedBookingId && (
                        <Box sx={{ mt: 1 }}>
                          <Button
                            variant='outlined'
                            size='small'
                            onClick={() => setBookingModalOpen(true)}
                          >
                            Buchung öffnen
                          </Button>
                        </Box>
                      )}
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Stack spacing={1}>
                      <Typography variant='body2'>
                        <strong>Erstellt am:</strong>{' '}
                        {voucher.meta?.createdAt
                          ? dayjs(voucher.meta.createdAt).format('DD.MM.YYYY HH:mm')
                          : '-'}
                      </Typography>
                      <Typography variant='body2'>
                        <strong>Erstellt von:</strong> {createdByDisplay}
                      </Typography>
                      {voucher.meta?.editedAt && (
                        <Typography variant='body2'>
                          <strong>Zuletzt bearbeitet am:</strong>{' '}
                          {dayjs(voucher.meta.editedAt).format('DD.MM.YYYY HH:mm')}
                        </Typography>
                      )}
                      {voucher.meta?.editedBy && (
                        <Typography variant='body2'>
                          <strong>Zuletzt bearbeitet von:</strong> {editedByDisplay}
                        </Typography>
                      )}
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Box display='flex' justifyContent='center' gap={2}>
                <Tooltip
                  title={
                    !canDownload
                      ? 'Keine Berechtigung zum Download'
                      : voucher.status === 'waitingForPayment'
                        ? 'Gutschein muss erst bezahlt werden'
                        : 'PDF anzeigen'
                  }
                >
                  <span>
                    <Button
                      variant='outlined'
                      startIcon={pdfLoading ? <CircularProgress size={16} /> : <GetAppIcon />}
                      onClick={handleDownloadPdf}
                      disabled={
                        !canDownload || voucher.status === 'waitingForPayment' || pdfLoading
                      }
                    >
                      {isCreator ? (
                        'PDF anzeigen'
                      ) : (
                        <LockIconWrapper right='voucher:special:download'>
                          PDF anzeigen
                        </LockIconWrapper>
                      )}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </>
          )}
        </Box>
      </Modal>
      {voucher?.relatedBookingId && (
        <BookingDetailsModal
          open={bookingModalOpen}
          onClose={() => setBookingModalOpen(false)}
          bookingId={voucher.relatedBookingId}
          onUpdate={() => {}}
        />
      )}
    </>
  );
}

export default ResourceVoucherDetailsModal;
